import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import '../styles/AuthComponents.css';

const client = generateClient({ authMode: 'userPool' });

interface UserProfileProps {
  user: any;
  onSignOut: () => void;
  refreshTrigger?: number;
}

interface UserQuota {
  dailyLimit: number;
  currentUsage: number;
  remainingCharacters: number;
  resetsAt: string;
}

const GET_USER_QUOTA = /* GraphQL */ `
  query GetUserQuota {
    getUserQuota {
      dailyLimit
      currentUsage
      remainingCharacters
      resetsAt
    }
  }
`;

const UserProfile: React.FC<UserProfileProps> = ({ user, refreshTrigger }) => {
  const [quota, setQuota] = useState<UserQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeUntilReset, setTimeUntilReset] = useState<string>('');

  const fetchQuota = async () => {
    try {
      const session = await fetchAuthSession();
      if (!session?.tokens?.accessToken) {
        throw new Error('No access token found.');
      }

      const result: any = await client.graphql({
        query: GET_USER_QUOTA,
        authToken: session.tokens.accessToken.toString(),
      });

      setQuota(result.data.getUserQuota);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch quota:', err);
      setError('Failed to load quota information');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchQuota();
  }, []);

  // Auto-refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchQuota();
    }
  }, [refreshTrigger]);

  // Update countdown timer
  useEffect(() => {
    if (!quota?.resetsAt) return;

    const updateCountdown = () => {
      const now = new Date();
      const resetTime = new Date(quota.resetsAt);
      const diff = resetTime.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeUntilReset('Resetting...');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setTimeUntilReset(`${hours}h ${minutes}m`);
      } else {
        setTimeUntilReset(`${minutes}m`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [quota?.resetsAt]);

  // Calculate usage percentage and color
  const getUsageInfo = () => {
    if (!quota) return { percentage: 0, color: '#28a745', label: 'good' };

    const percentage = (quota.currentUsage / quota.dailyLimit) * 100;

    if (percentage >= 90) {
      return { percentage, color: '#dc3545', label: 'critical' };
    } else if (percentage >= 70) {
      return { percentage, color: '#ffc107', label: 'warning' };
    }
    return { percentage, color: '#28a745', label: 'good' };
  };

  const usageInfo = getUsageInfo();

  return (
    <div className="quota-compact">
      {loading ? (
        <div className="quota-compact-loading">Loading...</div>
      ) : error ? (
        <div className="quota-compact-error">Error loading quota</div>
      ) : quota ? (
        <div className="quota-compact-display">
          <div className="quota-compact-text">
            {quota.remainingCharacters.toLocaleString()} / {quota.dailyLimit.toLocaleString()} chars remaining
          </div>
          <div className="quota-compact-tooltip">
            <div className="quota-tooltip-usage">
              Used: {quota.currentUsage.toLocaleString()} / {quota.dailyLimit.toLocaleString()} characters
            </div>
            <div className="quota-tooltip-remaining" style={{ color: usageInfo.color }}>
              Remaining: {quota.remainingCharacters.toLocaleString()}
            </div>
            <div className="quota-tooltip-reset">
              Resets in <strong>{timeUntilReset}</strong>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserProfile;
